/**
 * <localization-form> — country and language pickers.
 *
 * Previously defined inside the {% javascript %} block of sections/footer.liquid,
 * which made every localization picker on the site — including the two in
 * sections/header.liquid and the two in sections/header-mobile.liquid — silently
 * depend on the footer section being rendered. Moved here so the element is
 * defined globally from layout/theme.liquid regardless of which sections a
 * template happens to include.
 *
 * Behaviour is unchanged apart from a null guard on the toggle button.
 */
class LocalizationForm extends HTMLElement {
	constructor() {
		super();
		this.elements = {
			input: this.querySelector(
				'input[name="locale_code"], input[name="country_code"]'
			),
			button: this.querySelector('button'),
			panel: this.querySelector('ul'),
		};

		// A picker rendered with no available countries/languages has no button.
		if (!this.elements.button || !this.elements.panel) return;

		this.elements.button.addEventListener(
			'click',
			this.openSelector.bind(this)
		);
		this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

		this.querySelectorAll('a').forEach((item) =>
			item.addEventListener('click', this.onItemClick.bind(this))
		);
	}

	hidePanel() {
		if (!this.elements.button || !this.elements.panel) return;
		this.elements.button.setAttribute('aria-expanded', 'false');
		this.elements.panel.setAttribute('hidden', true);
	}

	onContainerKeyUp(event) {
		if (event.code.toUpperCase() !== 'ESCAPE') return;

		this.hidePanel();
		this.elements.button.focus();
	}

	onItemClick(event) {
		event.preventDefault();
		this.elements.input.value = event.currentTarget.dataset.value;
		this.querySelector('form')?.submit();
	}

	openSelector() {
		this.elements.button.focus();
		this.elements.panel.toggleAttribute('hidden');
		this.elements.button.setAttribute(
			'aria-expanded',
			(
				this.elements.button.getAttribute('aria-expanded') === 'false'
			).toString()
		);
	}

	connectedCallback() {
		this.handleClickOutside = this.handleClickOutside.bind(this);
		document.addEventListener('click', this.handleClickOutside);
	}

	disconnectedCallback() {
		document.removeEventListener('click', this.handleClickOutside);
	}

	handleClickOutside(event) {
		if (!this.contains(event.target)) {
			this.hidePanel();
		}
	}
}

if (!customElements.get('localization-form')) {
	customElements.define('localization-form', LocalizationForm);
}
